// React library is imported for building the component.
// Several components from Material UI are imported for styling and functionality like Box, Typography, Link, Paper, and Zoom.
// It also imports custom components PartnersContributors and StickyFooter (which seems to be absent in the provided code).
import * as React from "react";
import StickyFooter from "./StickyFooter";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Zoom from "@mui/material/Zoom";
import PartnersContributors from "./Partners";

// The code defines several styles using Javascript objects for logo images with different sizes. These styles are used with the img tag later.

const logoStyle = {
  width: "auto",
  height: "10vh",
  paddingLeft: "16px",
  marginTop: "16px",
  marginBottom: "16px",
};

const logoStyle2 = {
  width: "70%",
  height: "auto",
  margin: "auto",
  marginTop: 30,
};

const logoStyle3 = {
  width: "auto",
  height: 80,
  margin: "auto",
  marginTop: 20,
};
const logoStyle4 = {
  width: "auto",
  height: 40,
  margin: "auto",
  marginTop: 40,
  marginBottom: 20,
};

// This is the main component that returns JSX code defining the layout and content of the about us section.
// It uses a Box component from MUI to manage the layout with flexbox for different screen sizes (xs - extra small, md - medium).
// The component displays text content about ACASA using Typography components with various styles for headings and paragraphs.
// Links to relevant websites like CIMMYT and ACASA brochure are included using Link components.
// Images representing Workstreams of ACASA are displayed using the img tag with styles applied.
// It uses Zoom component from MUI to add a zoom animation effect on hover for the workstream logos.
// Partner logos from Bill & Melinda Gates Foundation and research institutions from South Asia are displayed using Paper components with logos as images and text descriptions. Links to their websites are also included.
// Similar structure is used to display information about Advisory Panel and Country Team Leads of ACASA.
// The component likely uses the StickyFooter component at the bottom (not provided in the code) for a persistent footer.

export default function AboutUs() {
  return (
    <div>
      <Box
        sx={{
          display: { xs: "flex", md: "flex" },
          flexDirection: "column",
          marginTop: "70px",
          minHeight: "50vh",
          textAlign: { sm: "center", md: "left" },
        }}
      >
        <Box sx={{ pb: 5 }}>
      {/* Header */}
      <Box
        sx={{
          mx: { xs: 2, sm: 7 },
          mt: { xs: 3, sm: 4 },
          textAlign: { xs: "center", md: "left" }, // center on desktop
        }}
      >
        <Typography
          sx={(theme) => ({
            color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
            fontWeight: 700,
            fontSize: { xs: "24px", sm: "34px" },
            fontFamily: "Poppins",
          })}
        >
          About ACASA
        </Typography>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // stacked on mobile
          mx: { xs: 2, sm: 7 },
          mt: 2,
          gap: { xs: 3, md: "3%" },
        }}
      >
        {/* Left Column */}
        <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 60%" } }}>
          <Typography
            sx={(theme) => ({
              color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
              fontSize: { xs: "16px", sm: "18px" },
              fontFamily: "Poppins",
              mt: 2,
              textAlign: {xs:'center',md:'left'},
            })}
          >
            There is an urgent need to identify strategies to manage increasing climate risks in agriculture. The Atlas of Climate Adaptation in South Asian Agriculture (ACASA) is a digital platform that comprehensively consolidates spatially explicit data on climate hazards, assessing their impact on smallholder populations, farms, crops, and livestock systems for the South Asian region. By evaluating the vulnerability of these populations, impact on region’s critical commodities, and the evidence around the effectiveness of gender informed adaptation options, ACASA-Bangladesh empowers decision-makers with valuable insights through a comprehensive adaptation portfolio to guide strategic investments and policy formulations.
          </Typography>

          <Typography
            sx={(theme) => ({
              color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
              fontSize: { xs: "16px", sm: "18px" },
              fontFamily: "Poppins",
              mt: 2,
              fontWeight: 500,
            })}
          >
            Read more about us:
          </Typography>

          <Typography
            sx={(theme) => ({
              color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
              fontFamily: "Poppins",
              fontSize: { xs: "16px", sm: "18px" },
              mt: 1,
            })}
          >
            <Link
              href={`${process.env.PUBLIC_URL}/ACASA Brochure_01.9.25_Single page HR.pdf`}
              underline="none"
              color="#4b9e44"
              target="_blank"
            >
              ACASA, Bangladesh Brochure
            </Link>
          </Typography>
        </Box>

        {/* Right Column - Workstreams */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "0 0 35%" },
            mt: { xs: 3, md: 2 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={(theme) => ({
              color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
              fontWeight: 600,
              fontFamily: "Poppins",
              fontSize: { xs: "20px", sm: "24px" },
              mb: 2,
              textAlign: { xs: "center", md: "left" }, // center on desktop
            })}
          >
            Workstreams
          </Typography>

          {[
            {
              icon: "work-stream1.svg",
              text: "Gridded risk analysis using historical crop yield data and satellite signatures; indicators of current and future hazards, exposure, and vulnerabilities",
              delay: 100,
            },
            {
              icon: "work-stream2.svg",
              text: "Climate impact on commodities under current and future climate",
              delay: 500,
            },
            {
              icon: "work-stream3.svg",
              text: "Heuristic models, crop/livestock models, statistical and econometric models, and expert consultations",
              delay: 900,
            },
            {
              icon: "work-stream4.svg",
              text: "An open-source, web-enabled, interactive, and dynamic Atlas development",
              delay: 1300,
            },
          ].map((ws, idx) => (
            <Zoom in={true} style={{ transitionDelay: `${ws.delay}ms` }} key={idx}>
              <Paper sx={{ mb: 2, p: 1, width: "100%", elevation: 0 }} elevation={0}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" }, // stack icon + text on mobile
                    alignItems: { xs: "center", sm: "center" },
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/${ws.icon}`}
                    style={{ ...logoStyle, marginBottom: { xs: 8, sm: 0 } }}
                    alt={`workstream ${idx + 1}`}
                    loading="lazy"
                  />
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                      fontSize: { xs: "14px", sm: "18px" },
                      fontFamily: "Poppins",
                      ml: { xs: 0, sm: 2 },
                      mt: { xs: 1, sm: 0 },
                    })}
                  >
                    {ws.text}
                  </Typography>
                </Box>
              </Paper>
            </Zoom>
          ))}
        </Box>
      </Box>
    </Box>
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", sm: "100%" },
            paddingBottom: 5,
            paddinTop: 5,
            backgroundColor: theme.palette.mode === "dark" ? "#1b1f23" : "#f7f7f7" /*Color for dark theme: 30363d */,
          })}
        >
          <Box sx={{ marginLeft: 7, marginRight: 7, marginTop: 4 }}>
            <Typography
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontWeight: "700",
                fontSize: "32px",
                fontFamily: "Poppins",
              })}
            >
              ACASA Development Partners
            </Typography>
            <Typography
              variant="h5"
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontWeight: "500",
                fontFamily: "Poppins",
                marginTop: 2,
                marginBottom: 2,
              })}
            >
              Partners and Contributors in Bangladesh
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" }, // stack on mobile
                gap: { xs: 3, sm: "1vw" }, // spacing responsive
                flexWrap: "wrap",
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              {/* Row 1 */}
              {/*
              <Box sx={{ display: "flex", flexDirection: "column", gap: "1vw" }}>
                <Link href="https://barc.gov.bd/" underline="none" target="_blank">
                  <Paper
                    sx={(theme) => ({
                      "m": 1,
                      "ml": 0,
                      "width": "21vw",
                      "height": 200,
                      "transition": "0.3s cubic-bezier(.47,1.64,.41,.8)",
                      "&:hover": {
                        boxShadow: theme.palette.mode === "dark" ? "0 4px 15px 0 rgba(255, 255, 255, 0.08)" : "0 4px 20px 0 rgba(0,0,0,0.12)",
                        transform: "scale(1.04)",
                      },
                    })}
                    elevation={0}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
                      <img src={`${process.env.PUBLIC_URL}/south-asia-1.png`} style={logoStyle3} alt="risk" loading="lazy" />
                      <Typography
                        sx={(theme) => ({
                          color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                          fontSize: "18px",
                          fontFamily: "Poppins",
                          margin: 2,
                          marginRight: 3,
                          marginLeft: 3,
                        })}
                      >
                        Bangladesh Agricultural Research Council (BARC)
                      </Typography>
                    </Box>
                  </Paper>
                </Link>

                <PartnersContributors country="bangladesh" />
              </Box>*/}

              {/* Row 2 */}
              {/*<Box sx={{ display: "flex", flexDirection: "column", gap: "1vw" }}>
                <Link href="https://icar.org.in/" underline="none" target="_blank">
                  <Paper
                    sx={(theme) => ({
                      "m": 1,
                      "ml": 0,
                      "width": "21vw",
                      "height": 200,
                      "transition": "0.3s cubic-bezier(.47,1.64,.41,.8)",
                      "&:hover": {
                        boxShadow: theme.palette.mode === "dark" ? "0 4px 15px 0 rgba(255, 255, 255, 0.08)" : "0 4px 20px 0 rgba(0,0,0,0.12)",
                        transform: "scale(1.04)",
                      },
                    })}
                    elevation={0}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
                      <img src={`${process.env.PUBLIC_URL}/south-asia-6.svg`} style={logoStyle3} alt="risk" loading="lazy" />
                      <Typography
                        sx={(theme) => ({
                          color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                          fontSize: "18px",
                          fontFamily: "Poppins",
                          margin: 2,
                          marginRight: 3,
                          marginLeft: 3,
                        })}
                      >
                        Indian Council of Agricultural Research (ICAR)
                      </Typography>
                    </Box>
                  </Paper>
                </Link>

                <PartnersContributors country="india" />
              </Box>*/}

              {/* Row 3 */}
              {/*<Box sx={{ display: "flex", flexDirection: "column", gap: "1vw" }}>
                <Link href="https://narc.gov.np/" underline="none" target="_blank">
                  <Paper
                    sx={(theme) => ({
                      "m": 1,
                      "ml": 0,
                      "width": "21vw",
                      "height": 200,
                      "transition": "0.3s cubic-bezier(.47,1.64,.41,.8)",
                      "&:hover": {
                        boxShadow: theme.palette.mode === "dark" ? "0 4px 15px 0 rgba(255, 255, 255, 0.08)" : "0 4px 20px 0 rgba(0,0,0,0.12)",
                        transform: "scale(1.04)",
                      },
                    })}
                    elevation={0}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
                      <img src={`${process.env.PUBLIC_URL}/south-asia-7.svg`} style={logoStyle3} alt="risk" loading="lazy" />
                      <Typography
                        sx={(theme) => ({
                          color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                          fontSize: "18px",
                          fontFamily: "Poppins",
                          margin: 2,
                          marginRight: 3,
                          marginLeft: 3,
                        })}
                      >
                        Nepal Agricultural Research Council (NARC)
                      </Typography>
                    </Box>
                  </Paper>
                </Link>

                <PartnersContributors country="nepal" />
              </Box>*/}

              {/* Row 4 */}
              <Box sx={{ 
                display: "flex",
                flexDirection: "column",
                gap: { xs: 3, sm: "1vw" },
                alignItems: { xs: "center", sm: "flex-start" }, // center on mobile
               }}>
                <Link href="https://doa.gov.lk/NRMC/" underline="none" target="_blank">
                  <Paper
                    sx={(theme) => ({
                      width: { xs: "100%", sm: "21vw" }, // full width on mobile, fixed on desktop
                      minHeight: 245,
                      transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
                      "&:hover": {
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 4px 15px 0 rgba(255, 255, 255, 0.08)"
                            : "0 4px 20px 0 rgba(0,0,0,0.12)",
                        transform: "scale(1.04)",
                      },
                    })}
                    elevation={0}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        textAlign: "center",
                        px: { xs: 2, sm: 0 }, // horizontal padding inside Paper
                        py: 2, // vertical padding
                      }}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/Home_imgs/BD Logos/barc_logo.png`}
                        style={logoStyle3}
                        alt="BARC Logo"
                        loading="lazy"
                      />
                      <Typography
                        sx={(theme) => ({
                          color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                          fontSize: { xs: "16px", sm: "18px" },
                          fontFamily: "Poppins",
                          mt: 2,
                          mb: 1,
                        })}
                      >
                        Bangladesh Agricultural Research Council (BARC), Bangladesh
                      </Typography>
                    </Box>
                  </Paper>

                </Link>

                <PartnersContributors country="bangladesh" />
              </Box>
            </Box>

            <Typography
              variant="h5"
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontWeight: "600",
                fontFamily: "Poppins",
                fontSize:'24px',
                marginTop: 2,
                marginBottom: 2,
              })}
            >
              External Partners
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 2, sm: 2, md: "1vw" },
                width: "100%",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {[
                {
                  href: "https://www.columbia.edu/",
                  img: `${process.env.PUBLIC_URL}/columbia-university.png`,
                  title: "Columbia University, USA",
                  imgStyle: logoStyle3,
                },
                {
                  href: "https://www.ufl.edu/",
                  img: `${process.env.PUBLIC_URL}/south-asia-11.svg`,
                  title: "University of Florida, USA",
                  imgStyle: logoStyle4,
                },
                {
                  href: "https://www.washington.edu/",
                  img: `${process.env.PUBLIC_URL}/Univ of Washington.png`,
                  title:
                    "Evans School Policy Analysis and Research (EPAR), University of Washington, USA",
                  imgStyle: {
                    width: "90%",
                    height: 40,
                    margin: "auto",
                    marginTop: 40,
                    marginBottom: 20,
                  },
                },
              ].map((item, index) => (
                <Link key={index} href={item.href} underline="none" target="_blank">
                  <Paper
                    elevation={0}
                    sx={(theme) => ({
                      width: {
                        xs: "100%",     // mobile
                        sm: "48%",      // tablet (2 per row)
                        md: 260,        // ✅ fixed desktop width (instead of vw)
                      },
                      minHeight: 245,   // ✅ same height for all
                      display: "flex",  // ✅ forces equal height behavior
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
                      "&:hover": {
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 4px 15px 0 rgba(255,255,255,0.08)"
                            : "0 4px 20px 0 rgba(0,0,0,0.12)",
                        transform: "scale(1.04)",
                      },
                    })}
                  >
                    <Box
                      sx={{
                        display: "flex",          // ✅ correct usage
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        p: 2,
                        width: "100%",
                      }}
                    >
                      <img
                        src={item.img}
                        style={item.imgStyle}
                        alt="logo"
                        loading="lazy"
                      />

                      <Typography
                        sx={(theme) => ({
                          color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                          fontSize: { xs: "16px", sm: "18px" },
                          fontFamily: "Poppins",
                          mt: 2,
                        })}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                  </Paper>

                </Link>
              ))}
            </Box>

            {/*<Box>
              <Typography
                variant="h5"
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "500",
                  fontFamily: "Poppins",
                  marginTop: 2,
                  marginBottom: 2,
                })}
              >
                Other Partners & Contributors
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: { xs: "100%", sm: "100%" },
                }}
                gap="1vw"
              >
                <PartnersContributors country="bangladesh" />
                <PartnersContributors country="india" />
                <PartnersContributors country="nepal" />
                <PartnersContributors country="sriLanka" />
              </Box>
            </Box>*/}
          </Box>
        </Box>
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", sm: "100%" },
            paddingBottom: 5,
            paddinTop: 5,
            backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
          })}
        >
          <Box sx={{ marginLeft: 7, marginRight: 7, marginTop: 4 }}>
            <Typography
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontWeight: "600",
                fontSize: "25px",
                fontFamily: "Poppins",
              })}
            >
              ACASA Advisory Panel
            </Typography>
            <Typography
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontSize: "15px",
                fontFamily: "Poppins",
                marginTop: 2,
                marginBottom: 2,
              })}
            >
              The advisory panel established under ACASA will have the scientific advisory committee that will identify potential users, use cases in different countries, and guide and review Atlas’
              progress.
            </Typography>
            
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",              // ✅ allow wrapping
                width: "100%",
                gap: 2,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {[ 
                {
                  name: "Pramod K Joshi",
                  title: "Ex-Director- International Food Policy Research Institute (IFPRI), India",
                },
                {
                  name: "Leigh Anderson",
                  title: "Evans School of Public Policy and Governance, University of Washington, Seattle",
                },
                {
                  name: "Miranda Meuwissen",
                  title: "Professor of Risk Management and Resilience, Wageningen University, Netherlands",
                },
                {
                  name: "Michiko Katagami",
                  title: "Principal Natural Resources and Agriculture Specialist, Asia Development Bank (ADB), Manila",
                },
                {
                  name: "V. Geethalakshmi",
                  title: "Vice Chancellor, Tamil Nādu Agriculture University, India",
                },
                {
                  name: "Alex Ruane",
                  title:
                    "Co-Director, Climate Impacts Group, NASA Goddard Institute for Space Studies (GISS)",
                },
                {
                  name: "Tess Russo",
                  title:
                    "Senior Program Officer, Bill & Melinda Gates Foundation (BMGF), Seattle",
                },
                {
                  name: "Pramod Aggarwal",
                  title:
                    "Project Leader, ACASA; Regional Program Leader, BISA-CIMMYT, Delhi",
                },
              ].map((item, idx) => (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={(theme) => ({
                    m: 0,
                    width: {
                      xs: "100%",     // mobile
                      sm: "48%",      // tablet (2 per row)
                      md: "21vw",     // desktop (unchanged)
                    },
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2c2c38" : "#f7f7ff",
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      textAlign: "center",
                      p: 2,
                    }}
                  >
                    <Typography
                      sx={(theme) => ({
                        color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                        fontSize: "15px",
                        fontWeight: 500,
                        fontFamily: "Poppins",
                        mb: 1,
                      })}
                    >
                      {item.name}
                    </Typography>

                    <Typography
                      sx={(theme) => ({
                        color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                        fontSize: "18px",
                        fontFamily: "Poppins",
                      })}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
       

            <Typography
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontWeight: "600",
                fontSize: "25px",
                fontFamily: "Poppins",
                marginTop: 2,
                marginBottom: 2,
              })}
            >
              ACASA Country Team Lead and Contact Points
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: { xs: "100%", sm: "100%" },
              }}
              gap="1vw"
            >
              {/*<Paper
                sx={(theme) => ({
                  m: 1,
                  ml: 0,
                  width: "21vw",
                  height: "auto",
                  backgroundColor: theme.palette.mode === "dark" ? "#322b32" : "#fff7ff",
                })}
                elevation={0}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    textAlign: { sm: "center", md: "center" },
                  }}
                >
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                      fontSize: "15px",
                      fontWeight: "500",
                      fontFamily: "Poppins",
                      margin: 2,
                      mb: 1,
                    })}
                  >
                    Hasan Md. Hamidur Rahman
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                      fontSize: "18px",
                      fontFamily: "Poppins",
                      margin: 0,
                      marginRight: 3,
                      marginLeft: 3,
                      mb: 2,
                    })}
                  >
                    Director, Computer and GIS Unit, BARC, Bangladesh
                  </Typography>
                </Box>
              </Paper>
              <Paper
                sx={(theme) => ({
                  m: 1,
                  ml: 0,
                  width: "21vw",
                  height: "auto",
                  backgroundColor: theme.palette.mode === "dark" ? "#322b32" : "#fff7ff",
                })}
                elevation={0}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    textAlign: { sm: "center", md: "center" },
                  }}
                >
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                      fontSize: "15px",
                      fontWeight: "500",
                      fontFamily: "Poppins",
                      margin: 2,
                      mb: 1,
                    })}
                  >
                    CA Rama Rao
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                      fontSize: "18px",
                      fontFamily: "Poppins",
                      margin: 0,
                      marginRight: 3,
                      marginLeft: 3,
                      mb: 2,
                    })}
                  >
                    Principal Scientist, ICAR-Central Research Institute for Dryland Agriculture (CRIDA), India
                  </Typography>
                </Box>
              </Paper>
              <Paper
                sx={(theme) => ({
                  m: 1,
                  ml: 0,
                  width: "21vw",
                  height: "auto",
                  backgroundColor: theme.palette.mode === "dark" ? "#322b32" : "#fff7ff",
                })}
                elevation={0}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    textAlign: { sm: "center", md: "center" },
                  }}
                >
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                      fontSize: "15px",
                      fontWeight: "500",
                      fontFamily: "Poppins",
                      margin: 2,
                      mb: 1,
                    })}
                  >
                    Roshan B Ojha
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                      fontSize: "18px",
                      fontFamily: "Poppins",
                      margin: 0,
                      marginRight: 3,
                      marginLeft: 3,
                      mb: 2,
                    })}
                  >
                    Soil Scientist, National Agricultural Environment Research Centre, NARC, Nepal
                  </Typography>
                </Box>
              </Paper>*/}
              <Paper
                sx={(theme) => ({
                    m: 0,
                    width: {
                      xs: "100%",     // mobile
                      sm: "48%",      // tablet (2 per row)
                      md: "21vw",     // desktop (unchanged)
                    },
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2c2c38" : "#f7f7ff",
                  })}
                elevation={0}
              >
                <Box
                  sx={{
                      display: "flex",
                      flexDirection: "column",
                      textAlign: "center",
                      p: 2,
                    }}
                >
                  <Typography
                    sx={(theme) => ({
                        color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                        fontSize: "15px",
                        fontWeight: 500,
                        fontFamily: "Poppins",
                        mb: 1,
                      })}
                  >
                    Hasan Md. Hamidur Rahman
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                        color: theme.palette.mode === "dark" ? "#e0e0e0" : "#333333",
                        fontSize: "18px",
                        fontFamily: "Poppins",
                      })}
                  >
                    Director (Computer & GIS), BARC, Bangladesh
                  </Typography>
                </Box>
              </Paper>
             
            </Box>
            <Typography
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontWeight: "600",
                fontSize: "24px",
                fontFamily: "Poppins",
                marginTop: 2,
                marginBottom: 2,
              })}
            >
              ACASA Core Team (Not According to Seniority)
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { xs: "100%", sm: "100%" },
              }}
              gap="0vw"
            >
              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                  fontWeight: "500",
                })}
              >
                1. Hasan Md. Hamidur Rahman, Director (Computer & GIS), BARC
                 and Natioanl Program Leader, ACASA, Bangladesh
              </Typography>
              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                2. Dr. Golam Mahboob, Chief Scientific Officer, NRM Division, BARC
              </Typography>
              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                3. Hasan Mahmud, Senior System Analyst, Computer & GIS Unit, BARC
              </Typography>
              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                4. Dr. Abdus Salam, Principal Scientific Officer, AERS Division, BARC
              </Typography>

              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                5. Dr. Faruque Ahmed, Director, Horticulture Research Centre, BARI
              </Typography>


              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                6. Dr. Apurbo K Chaki, Senior Scientific Officer, On-Farm Research Division, BARI
              </Typography>


              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                7. Dr. Debjit Roy, Senior Scientific Officer, Irrigation and Water Management Division, BRRI
              </Typography>


              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                8. Farhana Hoque, Deputy Director (Coordination), Field Service Wing, DAE
              </Typography>

            </Box>

            <Typography
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontWeight: "600",
                fontSize: "24px",
                fontFamily: "Poppins",
                marginTop: 2,
                marginBottom: 2,
              })}
            >
              Co-opt Member
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { xs: "100%", sm: "100%" },
              }}
              gap="0vw"
            >
              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                1. Dr. Ali Akbar Bhuiyan, Principal Scientific Officer, Livestock Division, BARC
              </Typography>
             
            </Box>


            <Typography
              sx={(theme) => ({
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                fontWeight: "600",
                fontSize: "24px",
                fontFamily: "Poppins",
                marginTop: 2,
                marginBottom: 2,
              })}
            >
              Project Members
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { xs: "100%", sm: "100%" },
              }}
              gap="0vw"
            >
              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                1. Md. Hasnain Ahmmed, Research Associate, ACASA Project, BARC
              </Typography>

              <Typography
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "#c4c4c4" : "#111111",
                  fontWeight: "normal",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                })}
              >
                2. Md. Mahfuzul Hasan, Research Assistant, ACASA Project, BARC
              </Typography>

            </Box>

          </Box>
        </Box>
      </Box>
      <StickyFooter></StickyFooter>
      {/*<Box
        sx={{
          marginTop: "80px",
          width: "100%",
          height: "calc(100vh - 80px)",
          alignItems: "center",
          justifyContent: "center",
          display: { xs: "flex", md: "none" },
        }}
      >
        <Typography>This website is designed for desktop. Please view in a bigger screen.</Typography>
      </Box>*/}
    </div>
  );
}